import Pubsub, { PubsubCallback } from '../pubsub/pubsub'
import {
  CAMPAIGN_PARAM,
  CART_RECOVERY_ACTION_PARAM,
  CLASSY_PARAMS,
  C_SRC_PARAM,
  C_SRC2_PARAM,
  DEDICATION_PARAM,
  DESIGNATION_PARAM,
  RECOVERY_TOKEN_PARAM,
  RECURRING_END_DATE_PARAM,
} from '../params/params.constants'
import { ClassyURLParams } from '../params/params.types'

const NAV_POPSTATE_EVENT = 'popstate'

export interface NavProps {
  win: Window
}

export interface NavState {
  campaign?: string
  params: ClassyURLParams
}

/**
 * Abstraction on top of url history related apis
 */
export default class Nav {
  private props: NavProps
  private state: NavState
  private pubsub: Pubsub<NavState>
  /**
   * Snapshot of query params from the initial URL restored by `removeClassyParams`:
   * pass-through keys (`c_src`, `c_src2`, `designation`, `recurringEndDate`), `utm_*`,
   * any other non-Classy param (excluding `campaign`). If none qualify at mount,
   * closing clears the query string.
   */
  private preserveParamsFromMount: Record<string, string>

  constructor(props: NavProps) {
    this.props = props
    this.preserveParamsFromMount = this._collectPreserveEligibleParamsFromUrl()
    this.state = this._getState()
    this._removeRecoveryParamsFromParentUrl()
    this.pubsub = new Pubsub()
    this.props.win.addEventListener(NAV_POPSTATE_EVENT, this._onPopstate)
  }

  public get = (): NavState => {
    return this.state
  }

  public destroy = (): void => {
    this.props.win.removeEventListener(NAV_POPSTATE_EVENT, this._onPopstate)
    this.pubsub.unsubscribeAll()
  }

  public setClassyParams = (campaign: string, newParams: ClassyURLParams): void => {
    const allParams = this._getAllParams()
    // Deleting instead of just overriding below coz we want the campaign param to come first in the url
    delete allParams.campaign
    const { recovery_token, cart_recovery_action, ...publicParams } = {
      campaign,
      ...allParams,
      ...newParams,
    }
    this._pushState(publicParams)

    this.state = this._getState()
    if (recovery_token) {
      this.state.params.recovery_token = recovery_token
    }
    if (cart_recovery_action) {
      this.state.params.cart_recovery_action = cart_recovery_action
    }
  }

  public removeClassyParams = (): void => {
    this._pushState(this.preserveParamsFromMount)
    this.state = this._getState()
  }

  public onPopstate = (callback: PubsubCallback<NavState>): void => {
    this.pubsub.subscribe(NAV_POPSTATE_EVENT, callback)
  }

  private _onPopstate = () => {
    this.state = this._getState()
    this._removeRecoveryParamsFromParentUrl()
    this.pubsub.publish({ name: NAV_POPSTATE_EVENT, data: this.state })
  }

  private _getState = () => ({
    campaign: this._getCampaign(),
    params: this._getClassyParams(),
  })

  private _getCampaign = () => {
    const allParams = this._getAllParams()
    const campaignParam = allParams[CAMPAIGN_PARAM]

    return campaignParam || undefined
  }

  private _getClassyParams = () => {
    const allParams = this._getAllParams()

    const classyParams = CLASSY_PARAMS.reduce((acc, cur) => {
      const value = allParams[cur]
      if (value) {
        // @ts-ignore
        acc[cur] = value
      }

      return acc
    }, {} as ClassyURLParams)

    const utmParams = Object.keys(allParams)
      .filter((key) => key.startsWith('utm_'))
      .reduce((acc, cur) => {
        return {
          ...acc,
          [cur]: allParams[cur as keyof ClassyURLParams],
        }
      }, {})

    return { ...classyParams, ...utmParams }
  }

  private _collectPreserveEligibleParamsFromUrl = (): Record<string, string> => {
    const allParams = this._getAllParams()
    const paramsToPreserve = new Set([
      C_SRC_PARAM,
      C_SRC2_PARAM,
      DEDICATION_PARAM,
      DESIGNATION_PARAM,
      RECURRING_END_DATE_PARAM,
    ])
    const preserved: Record<string, string> = {}

    for (const key of Object.keys(allParams)) {
      const value = allParams[key]
      if (!value) {
        continue
      }
      const isPreserved = paramsToPreserve.has(key)
      const isUtmParam = key.startsWith('utm_')
      const isClassyParam = CLASSY_PARAMS.includes(key)

      if (isPreserved || isUtmParam || (!isClassyParam && key !== CAMPAIGN_PARAM)) {
        preserved[key] = value
      }
    }

    return preserved
  }

  private _getAllParams = () => {
    const allParams = new URLSearchParams(this.props.win.location.search)
    return Object.fromEntries(allParams.entries())
  }

  /**
   * Capture recovery parameters in `state`, then remove them from the visible
   * host-page URL without creating another history entry. The iframe still
   * receives the captured values, while later analytics/navigation cannot
   * accidentally copy the recovery token from the address bar.
   */
  private _removeRecoveryParamsFromParentUrl = (): void => {
    const params = new URLSearchParams(this.props.win.location.search)

    if (!params.has(RECOVERY_TOKEN_PARAM) && !params.has(CART_RECOVERY_ACTION_PARAM)) {
      return
    }

    params.delete(RECOVERY_TOKEN_PARAM)
    params.delete(CART_RECOVERY_ACTION_PARAM)

    const search = params.toString()
    const { pathname, hash } = this.props.win.location
    const cleanedUrl = `${pathname}${search ? `?${search}` : ''}${hash || ''}`

    this.props.win.history.replaceState(undefined, '', cleanedUrl)
  }

  private _pushState = (queryObj: Record<string, string>) => {
    const queryStr = this._toQueryStr(queryObj)
    this.props.win.history.pushState(undefined, '', queryStr)
  }

  private _toQueryStr = (obj: Record<string, string>) => {
    const objKeys = Object.keys(obj)
    const { pathname, hash } = this.props.win.location
    const hashSuffix = hash || ''

    if (objKeys.length === 0) {
      return `${pathname}${hashSuffix}`
    }

    const queryStr = objKeys
      .filter((key) => !!obj[key])
      .map((key) => `${key}=${encodeURIComponent(obj[key])}`)
      .join('&')

    return `${pathname}?${queryStr}${hashSuffix}`
  }
}
